import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import { currencyFormat, dateTimeFormat, numberFormat } from '@fiverr-private/localization';
import { getContext } from '@fiverr-private/fiverr_context';
import { DEFAULT_CURRENCY } from './constants';
import { interpolateTemplate, getInUsd } from './utils';

export { DATETIME_ELEMENT_STYLES, NUMBER_STYLES } from '@fiverr-private/localization';

/** @type {React.Context<import('./index').LocalizationContextData | undefined>} */
const LocalizationContext = React.createContext({
    currency: DEFAULT_CURRENCY,
    currencyFormatFromUSD: getInUsd,
    dateTimeFormat,
    numberFormat,
});

/** @type {import('./index').UseLocalizationContext} */
export const useLocalizationContext = () => React.useContext(LocalizationContext);

/** @type {import('./index').LocalizationContextProvider} */
export const LocalizationContextProvider = ({ currency, children }) => {
    const { locale } = getContext();

    /** @type {import('./index').LocalizationContextData['currencyFormatFromUSD']} */
    const currencyFormatFromUSD = useCallback(
        (priceInUsd) => {
            let convertedPrice = priceInUsd * currency.rate;
            if (currency.forceRound && convertedPrice >= currency.forceRoundFromAmount) {
                convertedPrice = Math.ceil(convertedPrice);
            }
            const price = currencyFormat(convertedPrice, currency.name, {
                formattingLocale: locale,
                fallbackValue: interpolateTemplate(currency.template, convertedPrice),
            });
            return price;
        },
        [currency, locale]
    );

    /** @type {import('./index').LocalizationContextData['dateTimeFormat']} */
    const dateTimeFormatInternal = useCallback(
        (date, options = {}) => {
            const value = date instanceof Date ? date : new Date(date);
            return dateTimeFormat(value, { ...options, formattingLocale: locale });
        },
        [locale]
    );

    /** @type {import('./index').LocalizationContextData['numberFormat']} */
    const numberFormatInternal = useCallback(
        (value, options = {}) => numberFormat(value, { ...options, formattingLocale: locale }),
        [locale]
    );

    return (
        <LocalizationContext.Provider
            value={{
                currency,
                currencyFormatFromUSD,
                dateTimeFormat: dateTimeFormatInternal,
                numberFormat: numberFormatInternal,
            }}
        >
            {children}
        </LocalizationContext.Provider>
    );
};

LocalizationContextProvider.propTypes = {
    currency: PropTypes.shape({
        rate: PropTypes.number,
        name: PropTypes.string,
        template: PropTypes.string,
        forceRound: PropTypes.bool,
        forceRoundFromAmount: PropTypes.number,
    }),
    children: PropTypes.node,
};
